import { useBoundStore } from "@/store";
import {
  CaretRightIcon,
  CheckIcon,
  ConfettiIcon,
  ExcludeIcon,
  FilesIcon,
  HourglassHighIcon,
  LightbulbFilamentIcon,
  MaskHappyIcon,
  PencilLineIcon,
  SealCheckIcon,
  SwatchesIcon,
  TranslateIcon,
  VideoIcon,
} from "@phosphor-icons/react";
import { useCustomMutation, useGo, useOne, useUpdate } from "@refinedev/core";
import { Button, Flex, Form, Result, Spin, Steps, Typography } from "antd";
import { BigInput } from "components/BigInput/BigInput/";
import { MarkdownField } from "components/MarkdownField";
import PageHeader from "components/PageHeader";
import { ShadowButton } from "components/ShadowButton";
import { StyledMarkdownFieldWrapper } from "components/StyledMarkdownFieldWrapper";
import { useAnalytics } from "hooks/useAnalytics";
import { useAntTheme } from "hooks/useAntTheme";
import { t } from "i18n";
import { isEqual } from "lodash";
import {
  MediaProject,
  MediaProjectResponse,
  ProjectMediaResponse,
  ProjectTag,
} from "pages/media/types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { AnalyticsEvents } from "types/analytics";
import { isProjectHeyGenius } from "../helpers/helper";
import {
  convertProjectDocumentsToUploadFiles,
  getStoryboardAndInstanceData,
} from "../helpers/project-create-wizard";
import useHeyGeniusSaveMediaSettings from "../hooks/api/useHeyGeniusSaveMediaSettings";
import { ProjectType } from "../types";
import { useProjectWizardStore } from "../useProjectWizardStore";
import { ObjectivesFormItem } from "./ObjectivesFormItem";
import { ObjectivesSelect } from "./ObjectivesSelect";
import { ProjectSettingForm } from "./ProjectSettingForm";
import { SettingsApprovalScreen } from "./SettingsApproval";
import SpeakerView from "./SpeakerView";
import ToolbarWizard from "./ToolbarWizard";
import { radius } from "@libertify/ui";

const DEFAULT_DURATION = 120;

export const UPLOAD_STEP = "Upload";
export const OBJECTIVES_STEP = "Objectives";
const EXPERIENCE_STEP = "Experience";
const LANGUAGE_STEP = "Language";
const PAGES_STEP = "Pages";
const CONTENT_EXTRACTION_STEP = "ContentExtraction";
const DURATION_STEP = "Duration";
const TONE_STEP = "Tone";
const BRANDKIT_STEP = "BrandKit";
const TRANSITIONS_STEP = "Transitions";
const CONTACT_STEP = "Contact";
export const REVIEW_STEP = "Review";

const allSteps = [
  OBJECTIVES_STEP,
  EXPERIENCE_STEP,
  LANGUAGE_STEP,
  PAGES_STEP,
  CONTENT_EXTRACTION_STEP,
  DURATION_STEP,
  TONE_STEP,
  BRANDKIT_STEP,
  TRANSITIONS_STEP,
  CONTACT_STEP,
  REVIEW_STEP,
] as const;

const docustreamSteps = [
  OBJECTIVES_STEP,
  DURATION_STEP,
  BRANDKIT_STEP,
  CONTACT_STEP,
  REVIEW_STEP,
] as const;

const storyboardSteps = [
  LANGUAGE_STEP,
  PAGES_STEP,
  CONTENT_EXTRACTION_STEP,
  DURATION_STEP,
  TONE_STEP,
  REVIEW_STEP,
] as const;

const heyGeniusSteps = [
  OBJECTIVES_STEP,
  LANGUAGE_STEP,
  DURATION_STEP,
  TONE_STEP,
  BRANDKIT_STEP,
  CONTACT_STEP,
  REVIEW_STEP,
] as const;

export type Step = (typeof allSteps)[number];

const stepsSelector = (
  isSetupStoryboard: boolean,
  isHeyGenius: boolean
): Step[] => {
  if (isSetupStoryboard) {
    return [...storyboardSteps];
  }
  if (isHeyGenius) {
    return [...heyGeniusSteps];
  }
  return [...docustreamSteps];
};

// simple map to keep step name consistent with the setting_name property
// in ProjectSettingForm.tsx
const getSettingNameFromStep = (step: Step): string => {
  const stepToSettingNameMap: Record<string, string> = {
    [UPLOAD_STEP]: "upload",
    [OBJECTIVES_STEP]: "project_objective",
    [EXPERIENCE_STEP]: "experience_type",
    [LANGUAGE_STEP]: "storyboard_language",
    [PAGES_STEP]: "selected_pages",
    [CONTENT_EXTRACTION_STEP]: "summary_extraction",
    [DURATION_STEP]: "movie_duration",
    [TONE_STEP]: "experience_tone",
    [BRANDKIT_STEP]: "brandkit",
    [TRANSITIONS_STEP]: "video_transition",
    [CONTACT_STEP]: "contact",
    [REVIEW_STEP]: "review",
  };

  return (
    stepToSettingNameMap[step] ?? step.toLowerCase().replaceAll(/\s+/g, "_")
  );
};

export const ProjectCreateWizard = () => {
  const params = useParams();
  const setSidebarCollapsed = useBoundStore(
    (state) => state.setSidebarCollapsed
  );
  const go = useGo();
  // use onboarding event properties in this flow
  const { trackOnboardingEvent: trackEvent } = useAnalytics();

  const {
    project,
    setProject,
    step,
    setStep,
    resetProjectCreateState,
    wizardStartTime,
    playbookBypass,
  } = useProjectWizardStore((state) => ({
    project: state.project,
    setProject: state.setProject,
    step: state.step,
    setStep: state.setStep,
    resetProjectCreateState: state.resetProjectCreateState,
    wizardStartTime: state.wizardStartTime,
    playbookBypass: state.playbookBypass,
  }));
  const saveMediaSettings = useHeyGeniusSaveMediaSettings();
  const projectId = params.id;
  const isEdit = project?.status !== "WaitingForSettings";
  const isSetupStoryboard = params.setupType === "storyboard";
  const isHeyGenius = project && isProjectHeyGenius(project.type);
  const [projectType] = useState<ProjectType>(
    (project?.type as ProjectType) ?? "new_video"
  );

  const steps = stepsSelector(isSetupStoryboard, isHeyGenius);
  const stepIndex = steps.indexOf(step);
  const [maxStepIndexReached, setMaxStepIndexReached] = useState(0);

  const { mutateAsync: update } = useUpdate<MediaProject>();
  const { mutateAsync } = useCustomMutation();

  const { theme } = useAntTheme();
  const id = project?.id || projectId;
  const stepStartTimeRef = useRef<number | null>(wizardStartTime);
  const { data: projectData, isLoading: projectDataLoading } =
    useOne<MediaProject>({
      resource: "media/projects",
      id,
      queryOptions: {
        enabled: !!id && isEdit,
        onSuccess: (data) => {
          setProject(data.data);
        },
      },
    });

  const isDefaultProjectMode = !isHeyGenius && !isSetupStoryboard;
  const isPlaybookBypass = !!playbookBypass && !isEdit;

  const [form] = Form.useForm();

  // Use projectData for edit mode, or project from store for creation mode
  const projectForSettings = isEdit ? projectData?.data : project;
  const { storyboardId, storyboardSettings, instanceId, instanceSettings } =
    getStoryboardAndInstanceData(projectForSettings, params, isEdit);
  const projectUploadedFiles = convertProjectDocumentsToUploadFiles(
    projectForSettings?.documents
  );

  // TODO: this is how tones are handled in Lbertify app, tones are fetched from the backend, and they include an id and a prompt.
  // then in frontend we match them with the TONES in src/data/tones.ts which include object with keys that match with the tone ids from BE, and values that include the icon and the translation path.
  // BE should clarify if we need to follow the same logic for objectives or a different one.
  // const { data } = useList<ToneResponse>({
  //   resource: `media/tones`,
  //   queryOptions: {
  //     enabled: isDocustream(),
  //   },
  // });

  // Use null to distinguish "user hasn't made a selection yet" from "user selected nothing"
  const [userSelectedObjectives, setUserSelectedObjectives] = useState<
    string[] | null
  >(null);

  // Derive the actual value - use user selection if made, otherwise fall back to storyboard settings
  const selectedObjectives =
    userSelectedObjectives ?? storyboardSettings?.objectives ?? [];

  const getStepNumber = useCallback(
    (currentStep: Step): number => {
      return steps.indexOf(currentStep) + 1;
    },
    [steps]
  );

  const getWizardStepCompletionTrackingProps = useCallback(
    (completedStep: Step) => {
      return {
        step_number: getStepNumber(completedStep),
        step_name: getSettingNameFromStep(completedStep),
        step_duration: Math.round(
          (Date.now() - (stepStartTimeRef.current ?? Date.now())) / 1000
        ),
      };
    },
    [getStepNumber]
  );
  const handleSubmitObjectives = (values: {
    title: string;
    tags: ProjectTag[];
    description: string;
  }) => {
    const id = project?.id ?? projectId ?? "";
    trackEvent(AnalyticsEvents.ProjectSettingUpdate, {
      project_id: id,
      setting_name: "project_objective",
      setting_value: selectedObjectives.join(","),
      page: isEdit ? "project_settings" : "project_creation_wizard",
      ...getWizardStepCompletionTrackingProps(step),
    });

    void (async () => {
      try {
        await update({
          resource: "media/projects",
          id: id,
          values: {
            title: values.title,
            tags: values.tags,
          },
          successNotification() {
            return false;
          },
        });
        if (project.tags.some((tag) => tag.tag_key === "hey_genius_doc")) {
          await saveMediaSettings(
            project.organization_id,
            project.id,
            "default"
          );
        }
        const objectivesChanged = !isEqual(
          selectedObjectives,
          storyboardSettings?.objectives ?? []
        );
        const descriptionChanged =
          (values.description ?? "") !== (storyboardSettings?.brief ?? "");

        if (objectivesChanged || descriptionChanged) {
          await mutateAsync({
            url: `media/projects/${id}/storyboards/${storyboardId ?? project?.storyboards?.[0]?.id}/settings`,
            method: "patch",
            values: {
              brief: values.description,
              objectives: selectedObjectives,
            },
          });
        }
        nextStep();
      } catch (e) {
        console.error(e);
      }
    })();
  };

  const navigateTo = useNavigate();
  const goToProject = () => {
    navigateTo(`/media/projects/${project?.id}?newExperience`);
  };

  const trackStepCompletion = useCallback(
    (step: Step) => {
      if (!isEdit && stepStartTimeRef.current) {
        // keep a specific event for step completion
        // to build funnels easily, even if we also send
        // a ProjectSettingUpdate event for the same step
        // but the settings can be updated also in other pages
        trackEvent(
          AnalyticsEvents.ProjectWizardStepCompleted,
          getWizardStepCompletionTrackingProps(step)
        );
        // reset the step start time to track the next step
        stepStartTimeRef.current = Date.now();
      }
    },
    [trackEvent, getWizardStepCompletionTrackingProps, isEdit]
  );

  const nextStep = useCallback(() => {
    trackStepCompletion(step);

    const newStepIndex = stepIndex + 1;
    if (newStepIndex > maxStepIndexReached)
      setMaxStepIndexReached(newStepIndex);
    setStep(steps[newStepIndex]);
  }, [
    maxStepIndexReached,
    setStep,
    stepIndex,
    steps,
    trackStepCompletion,
    step,
  ]);

  const previousStep = () => {
    setStep(steps[stepIndex - 1]);
  };

  useEffect(() => {
    return () => {
      resetProjectCreateState();
    };
  }, [resetProjectCreateState]);

  useEffect(() => {
    setSidebarCollapsed(true);

    return () => {
      setSidebarCollapsed(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (storyboardSettings?.brief) {
      form.setFieldsValue({ description: storyboardSettings.brief });
    }
  }, [storyboardSettings?.brief, form]);

  // Initialize stepStartTimeRef from store if not already set
  useEffect(() => {
    if (wizardStartTime && !stepStartTimeRef.current) {
      stepStartTimeRef.current = wizardStartTime;
    }
  }, [wizardStartTime]);

  const emptyRequiredMark = (label: React.ReactNode) => <>{label}</>;
  const getProjectWizardSteps = (step: Step) => {
    const currentStepIndex = steps.indexOf(step);

    return (
      <Flex align="center" justify="center">
        <Steps
          current={currentStepIndex}
          labelPlacement="vertical"
          onChange={(value) => {
            setStep(steps[value]);
          }}
          items={steps.map((step, index) => {
            const description = t(
              "projects.components.ProjectCreateWizard.step." +
                step.split(" ").join("")
            );
            return {
              description,
              disabled: !isEdit && maxStepIndexReached < index,
              icon:
                index <= currentStepIndex ? (
                  <Flex
                    align="center"
                    justify="center"
                    style={{
                      backgroundColor: theme.colorPrimary,
                      width: 32,
                      height: 32,
                      borderRadius: radius.circle,
                      color: "white",
                    }}
                  >
                    <CheckIcon size={20} />
                  </Flex>
                ) : (
                  <Flex
                    align="center"
                    justify="center"
                    style={{
                      backgroundColor: theme.colorTextDescription,
                      width: 32,
                      height: 32,
                      borderRadius: radius.circle,
                      color: "white",
                      fontSize: 14,
                    }}
                  >
                    {index + 1}
                  </Flex>
                ),
            };
          })}
        />
      </Flex>
    );
  };

  // for edition
  if (projectDataLoading && isEdit) return <Spin spinning fullscreen />;

  const handleGoToStoryboard = () => {
    go({
      to: `/media/projects/${projectId}/storyboard/${storyboardId}`,
    });
  };

  const handleCancel = () => {
    if (!isSetupStoryboard && stepIndex !== 0) {
      previousStep();
    } else if (isSetupStoryboard && stepIndex === 0) {
      handleGoToStoryboard();
    }
  };

  return (
    <Flex>
      <Flex
        vertical
        gap={30}
        style={{ width: "100%" }}
        // workaround to force rerender when storyboardId or instanceId changes, at project creation
        // todo: find a better way to handle this
        key={`project-wizard-${storyboardId}-${instanceId}`}
      >
        {!isPlaybookBypass && getProjectWizardSteps(step)}

        <Flex
          vertical
          gap={40}
          style={{
            width: "100%",
            marginRight: "auto",
            marginLeft: "auto",
          }}
        >
          {step === OBJECTIVES_STEP && project && (
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmitObjectives}
              initialValues={project}
              requiredMark={emptyRequiredMark}
            >
              <PageHeader
                title={t(
                  "projects.components.ProjectCreateWizard.ObjectivesTitle"
                )}
                customElement={" ?"}
                subtitle={
                  isDefaultProjectMode
                    ? undefined
                    : t(
                        "projects.components.ProjectCreateWizard.createExceptionalStoryboard"
                      )
                }
              />
              <Flex
                align="center"
                justify="center"
                vertical
                style={{
                  width: "100%",
                  paddingBottom: 100,
                }}
              >
                <StyledFormItemWrapper>
                  <Form.Item
                    name="title"
                    rules={[
                      {
                        required: true,
                        message: t(
                          "projects.components.ProjectCreateWizard.aProjectName"
                        ),
                      },
                    ]}
                    style={{ width: "100%", marginBottom: 8 }}
                  >
                    <BigInput
                      style={{
                        width: "100%",
                        height: 50,
                      }}
                      suffix={
                        <PencilLineIcon
                          size={24}
                          color={theme.colorTextDescription}
                        />
                      }
                      styles={{
                        input: {
                          textAlign: "center",
                        },
                      }}
                    />
                  </Form.Item>
                </StyledFormItemWrapper>
                {isDefaultProjectMode && (
                  <ObjectivesSelect
                    selectedObjectives={selectedObjectives}
                    storyboardSettings={storyboardSettings}
                    setUserSelectedObjectives={setUserSelectedObjectives}
                  />
                )}
                <StyledFormItemWrapper>
                  <ObjectivesFormItem
                    isDefaultProjectMode={isDefaultProjectMode}
                  />
                </StyledFormItemWrapper>
              </Flex>
              <ToolbarWizard>
                <ShadowButton
                  htmlType="submit"
                  type="primary"
                  shape="round"
                  size="large"
                  style={{ minWidth: 120 }}
                  icon={<CaretRightIcon />}
                  iconPosition="end"
                >
                  {t("buttons.next")}
                </ShadowButton>
              </ToolbarWizard>
            </Form>
          )}
          {step === EXPERIENCE_STEP && project && (
            <ProjectSettingForm<string>
              projectId={project?.id}
              instanceId={instanceId}
              title={t("projects.components.ProjectCreateWizard.experience")}
              subtitle={t(
                "projects.components.ProjectCreateWizard.selectExperience"
              )}
              icon={<VideoIcon size={24} />}
              value={getExperienceValueFromProject(project, instanceSettings)}
              type={EXPERIENCE_STEP}
              onClose={() => nextStep()}
              onCancel={() => {
                handleCancel();
              }}
              cancelButtonLabel={t("buttons.back")}
              submitButtonLabel={t("buttons.next")}
            />
          )}
          {step === LANGUAGE_STEP && project && (
            <ProjectSettingForm<string>
              projectId={project?.id}
              storyboardId={storyboardId}
              title={t("projects.components.ProjectCreateWizard.Language")}
              subtitle={t(
                "projects.components.ProjectCreateWizard.chooseTheLanguage"
              )}
              icon={<TranslateIcon size={24} />}
              value={storyboardSettings?.language} // default from org
              type={LANGUAGE_STEP}
              onClose={() => nextStep()}
              onCancel={() => handleCancel()}
              cancelButtonLabel={t("buttons.back")}
              submitButtonLabel={t("buttons.next")}
            />
          )}
          {step === PAGES_STEP && project && (
            <ProjectSettingForm<string>
              projectId={project.id}
              storyboardId={storyboardId}
              title={t("projects.components.ProjectCreateWizard.Pages")}
              subtitle={t(
                "projects.components.ProjectCreateWizard.chooseThePages"
              )}
              icon={<FilesIcon size={24} />}
              type={PAGES_STEP}
              value={storyboardSettings?.selected_pages ?? undefined}
              onClose={() => nextStep()}
              onCancel={() => handleCancel()}
              cancelButtonLabel={t("buttons.back")}
              submitButtonLabel={t("buttons.next")}
              uploadedFiles={projectUploadedFiles}
            />
          )}
          {step === CONTENT_EXTRACTION_STEP && project && (
            <ProjectSettingForm<boolean>
              projectId={project.id}
              storyboardId={storyboardId}
              title={t(
                "projects.components.ProjectCreateWizard.ContentExtraction"
              )}
              subtitle={t(
                "projects.components.ProjectCreateWizard.chooseYourType"
              )}
              extra={
                <Flex justify="center" align="center">
                  <Flex
                    gap={20}
                    style={{ width: "80%" }}
                    justify="center"
                    align="center"
                  >
                    <LightbulbFilamentIcon
                      size={60}
                      color={theme.colorPrimary}
                    />
                    <Flex vertical>
                      <Typography.Text
                        type="secondary"
                        style={{ marginBottom: 10 }}
                      >
                        <strong>
                          {t(
                            "projects.components.ProjectCreateWizard.contentExtractionHint.titleExtractive"
                          )}
                        </strong>
                        <div>
                          {t(
                            "projects.components.ProjectCreateWizard.contentExtractionHint.textExtractive"
                          )}
                        </div>
                        <strong>
                          {t(
                            "projects.components.ProjectCreateWizard.contentExtractionHint.titleAbstractive"
                          )}
                        </strong>
                        <div>
                          {t(
                            "projects.components.ProjectCreateWizard.contentExtractionHint.textAbstractive"
                          )}
                        </div>
                      </Typography.Text>
                    </Flex>
                  </Flex>
                </Flex>
              }
              icon={<SealCheckIcon size={24} />}
              value={storyboardSettings?.preserve_original_doc ?? false}
              type="PreserveDocument"
              onClose={() => nextStep()}
              onCancel={() => handleCancel()}
              cancelButtonLabel={t("buttons.back")}
              submitButtonLabel={t("buttons.next")}
            />
          )}
          {step === DURATION_STEP && project && (
            <StyledDurationSection display={"1"} gap={40} vertical>
              <ProjectSettingForm<number>
                projectId={project?.id}
                storyboardId={storyboardId}
                title={t("projects.components.ProjectCreateWizard.Duration")}
                subtitle={t(
                  "projects.components.ProjectCreateWizard.chooseTheDuration"
                )}
                extra={
                  <Flex justify="center" align="center">
                    <Flex
                      gap={16}
                      align="center"
                      justify="space-around"
                      style={{
                        color: theme.colorTextDescription,
                        lineHeight: 1.1,
                      }}
                    >
                      <LightbulbFilamentIcon
                        size={60}
                        color={theme.colorPrimary}
                      />
                      {["Short", "Medium", "Long"].map((duration) => {
                        return (
                          <StyledMarkdownFieldWrapper key={duration}>
                            <strong style={{ margin: 0, padding: 0 }}>
                              {t(
                                `projects.components.ProjectCreateWizard.contentDuration.title${duration}`
                              )}
                            </strong>
                            <MarkdownField
                              value={t(
                                `projects.components.ProjectCreateWizard.contentDuration.text${duration}`
                              )}
                            />
                          </StyledMarkdownFieldWrapper>
                        );
                      })}
                    </Flex>
                  </Flex>
                }
                icon={<HourglassHighIcon size={24} />}
                value={storyboardSettings?.duration || DEFAULT_DURATION}
                type={DURATION_STEP}
                onClose={() => nextStep()}
                onCancel={() => handleCancel()}
                cancelButtonLabel={t("buttons.back")}
                submitButtonLabel={t("buttons.next")}
              />
            </StyledDurationSection>
          )}
          {step === TONE_STEP && project && (
            <ProjectSettingForm<string>
              projectId={project?.id}
              storyboardId={storyboardId}
              title={t("projects.components.ProjectCreateWizard.Tone")}
              subtitle={t(
                "projects.components.ProjectCreateWizard.chooseTheTone"
              )}
              icon={<MaskHappyIcon size={24} />}
              value={storyboardSettings?.tone_id ?? undefined}
              type={TONE_STEP}
              onClose={() => nextStep()}
              onCancel={() => handleCancel()}
              cancelButtonLabel={t("buttons.back")}
              submitButtonLabel={t("buttons.next")}
            />
          )}
          {step === BRANDKIT_STEP && project && (
            <ProjectSettingForm<string>
              projectId={project.id}
              instanceId={instanceId}
              title={t("projects.components.ProjectCreateWizard.BrandKit")}
              subtitle={t(
                "projects.components.ProjectCreateWizard.chooseTheBrand"
              )}
              icon={<SwatchesIcon size={24} />}
              type={BRANDKIT_STEP}
              value={project.settings.brand_kit_id ?? undefined}
              onClose={() => nextStep()}
              onCancel={() => handleCancel()}
              cancelButtonLabel={t("buttons.back")}
              submitButtonLabel={t("buttons.next")}
              isWizard={true}
            />
          )}
          {step === TRANSITIONS_STEP && project && (
            <ProjectSettingForm<string>
              projectId={project.id}
              instanceId={instanceId}
              title={t("projects.components.ProjectSettings.transition")}
              subtitle={t(
                "projects.components.ProjectSettings.chooseTransition"
              )}
              icon={<ExcludeIcon size={24} />}
              type={TRANSITIONS_STEP}
              value={instanceSettings?.transitions ?? "Basic"}
              onClose={nextStep}
              onCancel={handleCancel}
              cancelButtonLabel={t("buttons.back")}
              submitButtonLabel={t("buttons.next")}
            />
          )}
          {step === CONTACT_STEP && (
            <SpeakerView
              initialVoiceOnly={true}
              initialStep={"PRE_STEP"}
              showMoreFields={false}
              project={project}
              nextStep={nextStep}
              previousStep={handleCancel}
              scriptLanguage={storyboardSettings?.language.split("-")[0]}
            />
          )}
          {project && storyboardId && instanceId && step === REVIEW_STEP && (
            <>
              {project.settings.status === "Approved" ? (
                <Result
                  status="success"
                  icon={<ConfettiIcon size={60} color={theme.colorPrimary} />}
                  title={t(
                    "projects.components.ProjectCreateWizard.thanksYourRequirements"
                  )}
                  subTitle={t(
                    "projects.components.ProjectCreateWizard.weWillGenerate"
                  )}
                  extra={[
                    <Button
                      size="large"
                      key="new_project"
                      shape="round"
                      onClick={() => {
                        resetProjectCreateState();
                        go({
                          to: {
                            resource: "media_projects",
                            action: "create",
                          },
                        });
                      }}
                    >
                      {t(
                        "projects.components.ProjectCreateWizard.createAnotherProject"
                      )}
                    </Button>,
                    <Button
                      size="large"
                      type="primary"
                      key="project"
                      shape="round"
                      onClick={goToProject}
                    >
                      {t("projects.components.ProjectCreateWizard.openProject")}
                    </Button>,
                  ]}
                />
              ) : (
                <SettingsApprovalScreen
                  previousStep={
                    isPlaybookBypass
                      ? () => go({ to: "/media/projects/select-type" })
                      : previousStep
                  }
                  projectId={project.id}
                  storyboardId={storyboardId}
                  instanceId={instanceId}
                  stepNumber={getStepNumber(step)}
                  stepName={step.toLowerCase().replaceAll(/\s+/g, "_")}
                  playbookName={
                    isPlaybookBypass ? playbookBypass?.name : undefined
                  }
                />
              )}
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

interface StyledDurationSectionProps {
  display: string;
}

const StyledDurationSection = styled(Flex)<StyledDurationSectionProps>`
  .ant-tooltip {
    opacity: ${(props) => {
      return props.display;
    }};
    transition: opacity 0.3s ease-in-out;
  }
`;

const getExperienceValueFromProject = (
  project: MediaProjectResponse,
  instanceSettings: ProjectMediaResponse | undefined
) => {
  if (!project) return;

  const media_generation_mode = instanceSettings?.media_generation_mode;
  const include_document_animations =
    instanceSettings?.include_document_animations;
  const chat_mode = instanceSettings?.chat_mode;

  if (
    include_document_animations === false &&
    media_generation_mode === "Video" &&
    chat_mode === "Disabled"
  ) {
    return "video";
  } else if (
    include_document_animations === true &&
    media_generation_mode === "Video" &&
    chat_mode === "Disabled"
  ) {
    return "video_doc";
  } else if (
    include_document_animations === true &&
    media_generation_mode === "Video" &&
    chat_mode !== "Disabled"
  ) {
    return "video_doc_chat";
  }
};

const StyledFormItemWrapper = styled(Flex)`
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  max-width: 900px;
  margin: 16px auto;
`;
